import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useEnterpriseAccess } from '@/hooks/useEnterpriseAccess';
import { formatContractDate } from '@/lib/orgContract';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, CheckCircle2, Zap, Loader2, Building2, User, ExternalLink, AlertTriangle, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLicensePrice } from "@/hooks/useLicensePrice";
import { useUserAccess } from "@/hooks/useUserAccess";
import { useAuth } from "@/contexts/AuthContext";
import { checkSubscriptionStatus } from "@/lib/checkSubscription";
import { DiscountCodeInput, type DiscountResult } from "@/components/DiscountCodeInput";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getStoredReferral } from '@/lib/referral';
import { getFunctionErrorMessage } from '@/lib/functionError';
import { shouldOfferPro } from '@/lib/myPlanAccess';

interface PaymentGatewaySettings {
  provider: string;
  publishable_key: string;
  test_mode: boolean;
  enabled: boolean;
}

interface ActiveLicense {
  plan_type: string;
  plan_name: string;
  status: string;
}

const PaymentsSection = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [discount, setDiscount] = useState<DiscountResult | null>(null);
  const { price, label, periodLabel, isLoaded } = useLicensePrice();
  const { hasPaidAccess, hasProAccess, hasEnterpriseAccess, activeCohorts, accessError, isLoading: accessLoading } = useUserAccess();
  const { user } = useAuth();

  const { data: gatewayConfig } = useQuery<PaymentGatewaySettings | null>({
    queryKey: ['payment_gateway_config'],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'payment_gateway')
        .maybeSingle();
      return (data?.value as any) ?? null;
    },
    staleTime: 1000 * 60 * 10,
  });

  // Fetch active licenses for the user (only manual/courtesy + enterprise count locally)
  const { data: activeLicenses } = useQuery<(ActiveLicense & { provider: string | null })[]>({
    queryKey: ['user_active_licenses', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data } = await supabase
        .from('user_subscriptions')
        .select('status, provider, billing_plans!inner(plan_type, name)')
        .eq('user_id', user.id)
        .eq('status', 'active');
      if (!data) return [];
      return data.map((s: any) => ({
        plan_type: s.billing_plans?.plan_type || 'unknown',
        plan_name: s.billing_plans?.name || 'Licencia',
        status: s.status,
        provider: s.provider ?? null,
      }));
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });

  // Check subscription status from Stripe (already filtered by configured price)
  const { data: subscriptionInfo } = useQuery({
    queryKey: ['check_subscription', user?.id],
    queryFn: async () => {
      const data = await checkSubscriptionStatus();
      return data as {
        subscribed: boolean;
        status: string;
        cancel_at_period_end: boolean;
        current_period_end: string | null;
        past_due: boolean;
      };
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 2,
    retry: 1,
  });

  // Pro license is active only if:
  //  - Stripe says so (server-side filtered by configured price), OR
  //  - There's a manual/courtesy subscription record (provider null/manual)
  const hasProLicense = hasProAccess;
  const { data: entAccess } = useEnterpriseAccess();
  const hasEnterpriseLicense = hasEnterpriseAccess;
  const enterpriseExpired = !!entAccess?.isExpired;

  const paymentsEnabled = gatewayConfig?.enabled ?? false;
  const offerPro = shouldOfferPro({ hasPaidAccess, hasProAccess, hasEnterpriseAccess, loading: accessLoading, error: accessError });
  const showPlanCard = hasProLicense || hasEnterpriseLicense || offerPro;

  const handlePayment = async (force = false) => {
    if (!paymentsEnabled) {
      toast.info("Los pagos no están habilitados en este momento.");
      return;
    }
    setIsProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-license-checkout', {
        body: { discount_code_id: discount?.code_id || null, force, referral_code: getStoredReferral() },
      });

      if (error) throw error;

      if (data?.warning === 'already_has_enterprise' || data?.warning === 'already_has_stripe_subscription' || data?.warning === 'already_has_pro') {
        if (data?.warning !== 'already_has_enterprise') {
          toast.info(data.message || "Ya tienes una suscripción activa.");
          return;
        }
        const confirm = window.confirm(data.message);
        if (confirm) {
          await handlePayment(true);
        }
        return;
      }

      if (data?.free) {
        toast.success("¡Licencia Pro activada! Recarga la página para ver los cambios.");
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No se recibió URL de checkout");
      }
    } catch (err: any) {
      console.error("Payment error:", err);
      toast.error(await getFunctionErrorMessage(err, "Error al iniciar el pago"));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error("No se pudo abrir el portal de gestión");
      }
    } catch (err: any) {
      console.error("Portal error:", err);
      toast.error(err.message || "Error al abrir el portal de gestión");
    } finally {
      setIsPortalLoading(false);
    }
  };

  const features = [
    "Acceso a todo el contenido premium",
    "Rutas de autoaprendizaje a tu ritmo",
    "Diagnóstico de madurez en IA",
    "Comunidad de aprendizaje",
    "Asistente LIA completo",
    "Herramientas y plantillas",
    "Soporte prioritario",
  ];

  if (accessLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const renewalDate = subscriptionInfo?.current_period_end
    ? new Date(subscriptionInfo.current_period_end).toLocaleDateString('es-ES', {
        year: 'numeric', month: 'long', day: 'numeric',
      })
    : null;

  return (
    <div className="space-y-6">
      {accessError && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>No pudimos confirmar todos tus accesos. Recarga la página para comprobar tu plan.</AlertDescription>
        </Alert>
      )}
      {activeCohorts.length > 0 && (
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-primary" />Acceso activo por tu ruta</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeCohorts.map(cohort => (
              <div key={cohort.id} className="space-y-1">
                <p className="font-medium text-foreground">{cohort.programs?.name || 'Ruta'} — {cohort.name}</p>
                <p className="text-sm text-muted-foreground">
                  {cohort.content_access_deadline
                    ? `Acceso al contenido hasta el ${formatContractDate(cohort.content_access_deadline)}`
                    : 'Sin fecha límite de acceso configurada'}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {/* Past due alert */}
      {subscriptionInfo?.past_due && (
        <Alert className="border-amber-500/50 bg-amber-500/10">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-200">
            Tu método de pago falló. Actualiza tu método de pago para no perder el acceso.
            <Button
              variant="outline"
              size="sm"
              className="ml-3 border-amber-500/30 text-amber-500 hover:bg-amber-500/10"
              onClick={handleManageSubscription}
              disabled={isPortalLoading}
            >
              {isPortalLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3 mr-1" />}
              Actualizar método de pago
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Cancel at period end alert */}
      {subscriptionInfo?.cancel_at_period_end && !subscriptionInfo?.past_due && (
        <Alert className="border-blue-500/50 bg-blue-500/10">
          <CalendarClock className="h-4 w-4 !text-black" />
          <AlertDescription className="text-black">
            {renewalDate
              ? <>Tu suscripción fue cancelada pero mantienes acceso hasta el <strong className="text-black">{renewalDate}</strong>.</>
              : <>Tu suscripción fue cancelada pero mantienes acceso hasta el final del período actual.</>}
          </AlertDescription>
        </Alert>
      )}

      {enterpriseExpired && !hasProLicense && (
        <Alert>
          <Building2 className="h-4 w-4" />
          <AlertDescription>
            Plan de tu empresa vencido el {formatContractDate(entAccess?.contractEndDate)}. Tu historial sigue guardado.{activeCohorts.length > 0 ? ' Conservas el acceso por las rutas vigentes que se muestran arriba.' : ' Recuperarás el acceso Enterprise cuando tu empresa renueve.'}
          </AlertDescription>
        </Alert>
      )}

      {showPlanCard && (hasEnterpriseLicense && !hasProLicense ? (
        <Card className="glass-card overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-emerald-500 to-emerald-500/60" />
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-500" />
               Plan Enterprise
            </CardTitle>
            <CardDescription>
              Tu empresa te ha otorgado acceso completo a la plataforma.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            <div className="mt-6 space-y-2">
              <p className="text-sm font-medium text-foreground">Licencia activa:</p>
              <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 text-sm px-4 py-1.5 gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                Enterprise (asignada por tu empresa)
              </Badge>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="glass-card overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-primary to-primary/60" />
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
               Plan Pro
            </CardTitle>
            <CardDescription>
              {hasProLicense
                ? "Tu Plan Pro está activo. Disfruta de todos los beneficios."
                : "Adquiere tu licencia para acceder a todo el ecosistema Líder IA"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="text-center sm:text-left">
                <span className="text-4xl font-bold text-foreground">{isLoaded ? label : '…'}</span>
                <span className="text-muted-foreground text-lg">{periodLabel}</span>
              </div>
              <div className="flex-1">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Active licenses display */}
            {activeLicenses && activeLicenses.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-foreground">Licencias activas:</p>
                <div className="flex flex-wrap gap-2">
                  {hasEnterpriseLicense && (
                    <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 text-sm px-4 py-1.5 gap-1.5">
                      <Building2 className="w-3.5 h-3.5" />
                      Enterprise (asignada por tu empresa)
                    </Badge>
                  )}
                  {hasProLicense && (
                    <Badge className="bg-primary/20 text-primary border-primary/30 text-sm px-4 py-1.5 gap-1.5">
                      <User className="w-3.5 h-3.5" />
                      Pro (licencia individual)
                    </Badge>
                  )}
                </div>
                {hasEnterpriseLicense && hasProLicense && (
                  <Alert className="mt-2">
                    <AlertDescription className="text-xs">
                      Tienes ambas licencias activas. Si tu empresa desactiva la licencia Enterprise, conservarás tu acceso Pro individual.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Renewal info for Pro subscribers */}
            {hasProLicense && renewalDate && !subscriptionInfo?.cancel_at_period_end && (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarClock className="w-4 h-4" />
                Próxima renovación: {renewalDate}
              </div>
            )}

            <div className="mt-6 space-y-3">
              {hasPaidAccess && !hasProLicense && !hasEnterpriseLicense && (
                <Badge className="bg-emerald-500/20 text-emerald-500 border-emerald-500/30 text-sm px-4 py-1.5">
                  ✓ Acceso activo (cohorte)
                </Badge>
              )}

              {/* Manage subscription button for Pro users */}
              {hasProLicense && (
                <Button
                  variant="outline"
                  onClick={handleManageSubscription}
                  disabled={isPortalLoading}
                  className="gap-2"
                >
                  {isPortalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                  Gestionar Suscripción
                </Button>
              )}

              {offerPro && (
                <>
                  <DiscountCodeInput
                    originalPrice={price ?? 0}
                    context="subscription"
                    onApply={setDiscount}
                  />
                  {discount && (
                    <p className="text-sm text-muted-foreground">
                      Precio final: <span className="line-through">{label}</span>{" "}
                      <span className="font-bold text-foreground">${discount.final_price}</span>
                    </p>
                  )}
                  <Button
                    size="lg"
                    disabled={isProcessing || !paymentsEnabled}
                    onClick={() => handlePayment()}
                    className="gap-2"
                  >
                    {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    Adquirir Plan Pro
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {showPlanCard && <Card className="glass-card border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-foreground">
                {paymentsEnabled ? 'Pagos seguros con Stripe' : 'Pagos próximamente'}
              </h4>
              <p className="text-sm text-muted-foreground">
                {paymentsEnabled
                  ? 'Todos los pagos son procesados de forma segura a través de Stripe'
                  : 'La pasarela de pagos aún no ha sido configurada. Contacta al administrador.'}
              </p>
            </div>
            {!paymentsEnabled && (
              <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">Pendiente</Badge>
            )}
          </div>
        </CardContent>
      </Card>}
    </div>
  );
};

export default PaymentsSection;
